/**
 * PhotoProjectsManager Component
 * Canva-like project management for photo designs
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, FolderOpen, Image, Clock, Trash2, MoreVertical,
  Grid, List, ArrowLeft, Edit3, Copy, Loader2,
  Instagram, Facebook, Youtube, FileImage, Presentation, CreditCard
} from 'lucide-react';
import PhotoEditor from './PhotoEditor';
import PhotoProjectService from '../../../services/PhotoProjectService';

// Template presets
const TEMPLATE_PRESETS = {
  instagram_post: {
    id: 'instagram_post',
    label: 'Instagram Post',
    width: 2700,
    height: 3375,
    icon: Instagram,
    color: '#E1306C'
  },
  instagram_story: {
    id: 'instagram_story',
    label: 'Instagram Story',
    width: 1080,
    height: 1920,
    icon: Instagram,
    color: '#E1306C'
  },
  facebook_post: {
    id: 'facebook_post',
    label: 'Facebook Post',
    width: 1200,
    height: 630,
    icon: Facebook,
    color: '#1877F2'
  },
  youtube_thumbnail: {
    id: 'youtube_thumbnail',
    label: 'YouTube Thumbnail',
    width: 1280,
    height: 720,
    icon: Youtube,
    color: '#FF0000'
  },
  presentation: {
    id: 'presentation',
    label: 'Presentation',
    width: 1920,
    height: 1080,
    icon: Presentation,
    color: '#F97316'
  },
  business_card: {
    id: 'business_card',
    label: 'Business Card',
    width: 1050,
    height: 600,
    icon: CreditCard,
    color: '#8B5CF6'
  },
  poster: {
    id: 'poster',
    label: 'Poster',
    width: 1080,
    height: 1440,
    icon: FileImage,
    color: '#22C55E'
  },
  flyer: {
    id: 'flyer',
    label: 'Flyer',
    width: 850,
    height: 1100,
    icon: FileImage,
    color: '#0EA5E9'
  },
  custom: {
    id: 'custom',
    label: 'Custom Size',
    width: 1080,
    height: 1080,
    icon: Image,
    color: '#6B7280'
  }
};

const PhotoProjectsManager = ({ onBack, featureFlags = {} }) => {
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectMenuOpen, setProjectMenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [menuProject, setMenuProject] = useState(null);
  const [customWidth, setCustomWidth] = useState(1080);
  const [customHeight, setCustomHeight] = useState(1080);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const todayLabel = new Date().toDateString();

  // Fetch projects from API
  const fetchProjects = useCallback(async (page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await PhotoProjectService.listProjects({
        page,
        limit: 50,
        search: searchQuery,
        sort: sortBy,
        order: sortBy === 'name' ? 'asc' : 'desc'
      });
      setProjects(response.projects || []);
      setPagination(response.pagination || { page: 1, totalPages: 1, total: response.projects?.length || 0 });
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, sortBy]);

  // Load projects on mount and when search/sort changes
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Create new project
  const createProject = useCallback(async (templateId) => {
    const template = TEMPLATE_PRESETS[templateId];
    const width = templateId === 'custom' ? customWidth : template.width;
    const height = templateId === 'custom' ? customHeight : template.height;

    setIsCreating(true);
    try {
      const newProject = await PhotoProjectService.createProject({
        name: 'Untitled Design',
        templateId,
        width,
        height
      });
      setShowNewProjectModal(false);
      setEditingProject(newProject);
      // Refresh projects list
      fetchProjects();
    } catch (err) {
      console.error('Failed to create project:', err);
      alert('Failed to create project. Please try again.');
    } finally {
      setIsCreating(false);
    }
  }, [customWidth, customHeight, fetchProjects]);

  // Save project (called from editor)
  const handleSaveProject = useCallback(async (projectData) => {
    try {
      const updated = await PhotoProjectService.updateProject(projectData.id, {
        name: projectData.name,
        elements: projectData.elements,
        backgroundColor: projectData.backgroundColor,
        thumbnail: projectData.thumbnail
      });

      // Update local state
      setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
      setEditingProject(updated);
    } catch (err) {
      console.error('Failed to save project:', err);
      alert('Failed to save project. Please try again.');
    }
  }, []);

  // Delete project
  const deleteProject = useCallback(async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      setProjectMenuOpen(null);
      return;
    }

    try {
      await PhotoProjectService.deleteProject(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch (err) {
      console.error('Failed to delete project:', err);
      alert('Failed to delete project. Please try again.');
    }
    setProjectMenuOpen(null);
  }, []);

  // Duplicate project
  const duplicateProject = useCallback(async (project) => {
    try {
      const duplicated = await PhotoProjectService.duplicateProject(project.id, `${project.name} (Copy)`);
      setProjects(prev => [duplicated, ...prev]);
    } catch (err) {
      console.error('Failed to duplicate project:', err);
      alert('Failed to duplicate project. Please try again.');
    }
    setProjectMenuOpen(null);
  }, []);

  // Rename project
  const renameProject = useCallback(async (projectId, newName) => {
    try {
      const updated = await PhotoProjectService.updateProject(projectId, { name: newName });
      setProjects(prev => prev.map(p => p.id === projectId ? updated : p));
    } catch (err) {
      console.error('Failed to rename project:', err);
      alert('Failed to rename project. Please try again.');
    }
  }, []);

  // Filter projects (search is now done server-side, but we keep local filtering for instant feedback)
  const filteredProjects = projects;
  const updatedToday = projects.filter((project) => new Date(project.updatedAt).toDateString() === todayLabel).length;

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // If editing a project, show the editor
  if (editingProject) {
    return (
      <PhotoEditor
        project={editingProject}
        onBack={() => {
          setEditingProject(null);
          fetchProjects(); // Refresh list when returning
        }}
        onSave={handleSaveProject}
        featureFlags={featureFlags}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-[#f7f4ee] dark:bg-slate-950">
      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Hero Section */}
        <div className="relative overflow-hidden px-6 py-10 sm:py-12">
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(120deg, rgba(13, 148, 136, 0.98), rgba(245, 158, 11, 0.95))'
            }}
          />
          <div
            className="absolute inset-0 opacity-70"
            style={{
              backgroundImage: 'radial-gradient(circle at 15% 15%, rgba(255,255,255,0.45), transparent 40%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.35), transparent 35%)'
            }}
          />
          <div className="relative max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="text-white">
                <p className="text-sm uppercase tracking-[0.2em] text-white/70">Design workspace</p>
                <h2 className="text-3xl sm:text-4xl font-semibold mt-2">All photo projects</h2>
                <p className="text-sm sm:text-base text-white/80 mt-2 max-w-xl">
                  Start from a high-performing template or open a recent design to refine your marketing collateral.
                </p>
              </div>
              <div className="w-full lg:w-[360px]">
                <label className="text-xs font-semibold text-white/80 uppercase tracking-[0.2em]">
                  Search library
                </label>
                <div className="relative mt-3">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search across all designs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white/95 dark:bg-slate-800 border-0 rounded-xl shadow-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-teal-200"
                  />
                </div>
                <div className="flex flex-wrap gap-2 mt-4 text-xs text-white/80">
                  <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                    {pagination.total} designs
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                    {updatedToday} updated today
                  </span>
                </div>
                <button
                  onClick={() => setShowNewProjectModal(true)}
                  className="mt-4 w-full px-4 py-2.5 bg-white text-slate-900 hover:bg-white/90 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create Design
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Start Templates */}
        <div className="px-6 -mt-8">
          <div className="bg-white/95 dark:bg-slate-900/80 backdrop-blur rounded-2xl shadow-xl border border-white/60 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Quick start</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Pick a size, customize, and publish in minutes.
                </p>
              </div>
              <button
                onClick={() => setShowNewProjectModal(true)}
                className="text-sm font-medium text-teal-700 hover:text-teal-600"
              >
                View all templates
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.values(TEMPLATE_PRESETS).filter(t => t.id !== 'custom').slice(0, 6).map((template) => {
                const TemplateIcon = template.icon;
                return (
                  <button
                    key={template.id}
                    onClick={() => createProject(template.id)}
                    disabled={isCreating}
                    className="group p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/80 hover:border-teal-400 hover:bg-white dark:hover:bg-slate-800 transition-all text-left disabled:opacity-50"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                      style={{ backgroundColor: `${template.color}20` }}
                    >
                      <TemplateIcon className="w-5 h-5" style={{ color: template.color }} />
                    </div>
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white">{template.label}</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {template.width} x {template.height}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Filters and View Options */}
        <div className="px-6 py-4 mt-6 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/80 backdrop-blur flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <option value="updatedAt">Date modified</option>
                <option value="createdAt">Date created</option>
                <option value="name">Name</option>
              </select>
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              {filteredProjects.length} results
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Projects Section */}
        <div className="p-6">
          {/* Loading State */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-12 h-12 text-teal-600 animate-spin mb-4" />
              <p className="text-slate-500 dark:text-slate-400">Loading your designs...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                {error}
              </h3>
              <button
                onClick={() => fetchProjects()}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              {projects.length === 0 ? (
                <>
                  <FolderOpen className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Your design library is empty
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-6">
                    Start with a template or choose a custom size to create your first design.
                  </p>
                  <button
                    onClick={() => setShowNewProjectModal(true)}
                    className="px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-medium transition-colors inline-flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Create Design
                  </button>
                </>
              ) : (
                <>
                  <Search className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                    No results found
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    Try a different search term
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                Recent designs
              </h3>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {filteredProjects.map((project) => {
                    const template = TEMPLATE_PRESETS[project.templateId] || TEMPLATE_PRESETS.custom;
                    const TemplateIcon = template.icon;
                    // Check for non-empty thumbnail (empty string should be treated as no thumbnail)
                    const rawThumbnail = project.thumbnail || project.thumbnailUrl || project.thumbnail_url;
                    const projectThumbnail = rawThumbnail && rawThumbnail.length > 10 ? rawThumbnail : null;

                    return (
                      <div
                        key={project.id}
                        className="group relative bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer"
                        onClick={() => setEditingProject(project)}
                      >
                        {/* Thumbnail */}
                        <div className="aspect-[4/3] bg-slate-100 dark:bg-slate-700 relative">
                          {projectThumbnail ? (
                            <img
                              src={projectThumbnail}
                              alt={project.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={`w-full h-full flex items-center justify-center ${projectThumbnail ? 'hidden' : ''}`}>
                            <Image className="w-12 h-12 text-slate-300 dark:text-slate-600" />
                          </div>
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                            <span className="opacity-0 group-hover:opacity-100 px-3 py-1.5 bg-white dark:bg-slate-800 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 shadow-lg transition-opacity">
                              Open
                            </span>
                          </div>
                        </div>
                        {/* Info */}
                        <div className="p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                {project.name}
                              </h4>
                              <div className="flex items-center gap-1.5 mt-1">
                                <TemplateIcon className="w-3.5 h-3.5" style={{ color: template.color }} />
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                  {template.label} · {formatRelativeTime(project.updatedAt)}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (projectMenuOpen === project.id) {
                                  setProjectMenuOpen(null);
                                  setMenuProject(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  // Check if menu would go off screen bottom, if so position above
                                  const menuHeight = 130;
                                  const spaceBelow = window.innerHeight - rect.bottom;
                                  const top = spaceBelow < menuHeight ? rect.top - menuHeight - 4 : rect.bottom + 4;
                                  setMenuPosition({
                                    top,
                                    left: Math.min(rect.right - 160, window.innerWidth - 168)
                                  });
                                  setProjectMenuOpen(project.id);
                                  setMenuProject(project);
                                }
                              }}
                              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                                              </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredProjects.map((project) => {
                    const template = TEMPLATE_PRESETS[project.templateId] || TEMPLATE_PRESETS.custom;
                    const TemplateIcon = template.icon;
                    const rawThumbnail = project.thumbnail || project.thumbnailUrl || project.thumbnail_url;
                    const projectThumbnail = rawThumbnail && rawThumbnail.length > 10 ? rawThumbnail : null;
                    return (
                      <div
                        key={project.id}
                        className="group flex items-center gap-4 p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => setEditingProject(project)}
                      >
                        {/* Thumbnail */}
                        <div className="w-16 h-12 bg-slate-100 dark:bg-slate-700 rounded overflow-hidden flex-shrink-0 relative">
                          {projectThumbnail ? (
                            <img
                              src={projectThumbnail}
                              alt={project.name}
                              className="absolute inset-0 w-full h-full object-cover object-center"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement?.querySelector('.thumbnail-fallback')?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={`thumbnail-fallback absolute inset-0 flex items-center justify-center ${projectThumbnail ? 'hidden' : ''}`}>
                            <Image className="w-6 h-6 text-slate-300 dark:text-slate-600" />
                          </div>
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-slate-900 dark:text-white truncate">
                            {project.name}
                          </h4>
                          <div className="flex items-center gap-3 mt-1">
                            <div className="flex items-center gap-1">
                              <TemplateIcon className="w-3.5 h-3.5" style={{ color: template.color }} />
                              <span className="text-xs text-slate-500">{template.label}</span>
                            </div>
                            <span className="text-xs text-slate-400">
                              {project.width} x {project.height}
                            </span>
                          </div>
                        </div>
                        {/* Edited time */}
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Clock className="w-3.5 h-3.5" />
                          {formatRelativeTime(project.updatedAt)}
                        </div>
                        {/* Actions */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (projectMenuOpen === project.id) {
                              setProjectMenuOpen(null);
                              setMenuProject(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const menuHeight = 130;
                              const spaceBelow = window.innerHeight - rect.bottom;
                              const top = spaceBelow < menuHeight ? rect.top - menuHeight - 4 : rect.bottom + 4;
                              setMenuPosition({
                                top,
                                left: Math.min(rect.right - 160, window.innerWidth - 168)
                              });
                              setProjectMenuOpen(project.id);
                              setMenuProject(project);
                            }
                          }}
                          className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/70">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                Start a new design
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Select a template size or define your own canvas.
              </p>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {isCreating && (
                <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 flex items-center justify-center z-10">
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-2" />
                    <p className="text-sm text-slate-600 dark:text-slate-400">Creating project...</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {Object.values(TEMPLATE_PRESETS).filter(t => t.id !== 'custom').map((template) => {
                  const TemplateIcon = template.icon;
                  return (
                    <button
                      key={template.id}
                      onClick={() => createProject(template.id)}
                      disabled={isCreating}
                      className="group p-4 bg-slate-50 dark:bg-slate-800 hover:bg-white dark:hover:bg-slate-700 rounded-xl border-2 border-transparent hover:border-teal-400 transition-all text-left disabled:opacity-50"
                    >
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center mb-3"
                        style={{ backgroundColor: `${template.color}20` }}
                      >
                        <TemplateIcon className="w-6 h-6" style={{ color: template.color }} />
                      </div>
                      <h3 className="font-medium text-slate-900 dark:text-white text-sm">
                        {template.label}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {template.width} x {template.height} px
                      </p>
                    </button>
                  );
                })}
                {/* Custom Size */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-3 bg-slate-200 dark:bg-slate-700">
                    <Image className="w-6 h-6 text-slate-500" />
                  </div>
                  <h3 className="font-medium text-slate-900 dark:text-white text-sm mb-2">
                    Custom Size
                  </h3>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="number"
                      value={customWidth}
                      onChange={(e) => setCustomWidth(parseInt(e.target.value) || 100)}
                      className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded"
                      placeholder="Width"
                    />
                    <span className="text-slate-400 self-center">x</span>
                    <input
                      type="number"
                      value={customHeight}
                      onChange={(e) => setCustomHeight(parseInt(e.target.value) || 100)}
                      className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded"
                      placeholder="Height"
                    />
                  </div>
                  <button
                    onClick={() => createProject('custom')}
                    disabled={isCreating}
                    className="w-full py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded transition-colors disabled:opacity-50"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-end">
              <button
                onClick={() => setShowNewProjectModal(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dropdown Menu - rendered at root level to escape overflow:hidden */}
      {projectMenuOpen && menuProject && (
        <>
          {/* Click outside to close menu */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setProjectMenuOpen(null);
              setMenuProject(null);
            }}
          />
          {/* Menu */}
          <div
            className="fixed z-50 w-40 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1"
            style={{
              top: menuPosition.top,
              left: menuPosition.left
            }}
          >
            <button
              onClick={() => {
                const newName = window.prompt('Enter new name:', menuProject.name);
                if (newName && newName.trim()) {
                  renameProject(menuProject.id, newName.trim());
                }
                setProjectMenuOpen(null);
                setMenuProject(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
            >
              <Edit3 className="w-4 h-4" />
              Rename
            </button>
            <button
              onClick={() => {
                duplicateProject(menuProject);
                setMenuProject(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Duplicate
            </button>
            <button
              onClick={() => {
                deleteProject(menuProject.id);
                setMenuProject(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoProjectsManager;
